import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Autocomplete,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ClickAwayListener,
  Checkbox,
} from "@mui/material";
import { Add, Delete, Close } from "@mui/icons-material";
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { OrderContext } from "../contexts/OrderContext";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";

// Base URL for API calls, defaults to localhost if not set in environment
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

function UsersGroupMaster() {
  const { storeData } = useContext(OrderContext);
  const { customers = [] } = storeData; // All registered users from DB
  const navigate = useNavigate();

  const selectedManufacturer = JSON.parse(localStorage.getItem("selectedMfr"));
  const mfrId = selectedManufacturer?.id;

  // --- State Variables ---
  const [groups, setGroups] = useState([]); // List of all user groups
  const [selectedGroup, setSelectedGroup] = useState(null); // The currently selected group
  const [newGroupName, setNewGroupName] = useState(""); // Input for creating a new group
  const [customersAdded, setCustomersAdded] = useState([]); // Customers already in the selected group (fetched from API)
  const [selectedCustomer, setSelectedCustomer] = useState(null); // The customer selected from the Autocomplete dropdown
  const [searchTerm, setSearchTerm] = useState(""); // Input value for the customer search/Autocomplete
  const [highlightedIds, setHighlightedIds] = useState([]); // IDs to highlight (unused but kept)
  const [openSnackbar, setOpenSnackbar] = useState(false); // Controls Snackbar visibility
  const [snackbarSeverity, setSnackbarSeverity] = useState(""); // Snackbar type (e.g., "success", "warning")
  const [snackbarMsg, setSnackbarMsg] = useState(""); // Snackbar message
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingGroupUsers, setLoadingGroupUsers] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const allSelected =
    customersAdded.length > 0 &&
    selectedCustomerIds.length === customersAdded.length;

  // local form mirror for mobile/email/note and id
  const [formData, setFormData] = useState({
    id: "",
    name: "",
    mobile: "",
    email: "",
    note: "",
  });

  const [isSendingWA, setIsSendingWA] = useState(false);

  const [waSnackbarOpen, setWaSnackbarOpen] = useState(false);
  const [waSnackbarSeverity, setWaSnackbarSeverity] = useState("success");
  const [waSnackbarMsg, setWaSnackbarMsg] = useState("");

  // --- Add these states ---
  const [isWhatsappDialogOpen, setIsWhatsappDialogOpen] = useState(false);
  const [waText, setWaText] = useState("");

  // WhatsApp file-handling states
  const [waFile, setWaFile] = useState(null);
  const [waFilePreview, setWaFilePreview] = useState(null);
  const [waFileName, setWaFileName] = useState("");
  const [waIsImage, setWaIsImage] = useState(false);

  const [allCustomerPhone, setAllCustomerPhone] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // ====================== Effects and Functions ======================
  // Effect to fetch all groups for the current manufacturer on initial load
  useEffect(() => {
    const fetchGroups = async () => {
      if (!mfrId) return;
      setLoadingGroups(true);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/auth/user-groups/${mfrId}`,
        );
        // assume res.data.data is array of groups
        const fetched = res?.data?.data ?? [];
        setGroups(fetched);
      } catch (err) {
        console.error("Error fetching groups:", err);
      } finally {
        setLoadingGroups(false);
      }
    };

    fetchGroups();
  }, [mfrId]);

  // Converts a date string or object into "dd-mm-yyyy, hh:mm" format
  const formatDateTime = (dateInput) => {
    if (!dateInput) return "";

    const date = new Date(dateInput);

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${day}-${month}-${year}, ${hours}:${minutes}`;
  };

  // Defensive fetch for customers in a group. Tries a couple of plausible endpoints/responses.
  const fetchCustomersInGroup = async (groupId) => {
    if (!groupId) return;
    setLoadingGroupUsers(true);
    try {
      // Try an endpoint that might return users for a group
      let res;
      // Primary attempt: endpoint used in your original code
      try {
        res = await axios.get(
          `${API_BASE_URL}/api/auth/user-groups/${mfrId}/${groupId}`,
        );
      } catch (e) {
        // fallback: try typical REST endpoint
        res = await axios.get(
          `${API_BASE_URL}/api/auth/user-groups/${groupId}/users`,
        );
      }

      // Normalize different response shapes
      let users = [];
      if (res?.data?.data && Array.isArray(res.data.data)) {
        users = res.data.data;
      } else if (res?.data?.users && Array.isArray(res.data.users)) {
        users = res.data.users;
      } else if (Array.isArray(res?.data)) {
        users = res.data;
      } else {
        // unknown shape: keep empty
        users = [];
      }

      setCustomersAdded(users);
      setSelectedCustomerIds([]);
      setAllCustomerPhone(
        users.map((user) => {
          let num = user.mobile.replace(/\s+/g, ""); // remove all spaces

          if (!num.startsWith("+91")) {
            num = "+91" + num;
          }

          return num;
        }),
      );
    } catch (err) {
      console.error("Error fetching customers in group:", err);
      setCustomersAdded([]);
    } finally {
      setLoadingGroupUsers(false);
    }
  };

  // refetch group users when selectedGroup changes
  useEffect(() => {
    if (selectedGroup) {
      fetchCustomersInGroup(selectedGroup.id);
    } else {
      setCustomersAdded([]);
    }
  }, [selectedGroup]);

  // Prevent persistent checkbox selection after switching groups
  useEffect(() => {
    if (selectedGroup) {
      fetchCustomersInGroup(selectedGroup.id);
      setSelectedCustomerIds([]); // <-- ADD THIS
    } else {
      setCustomersAdded([]);
      setSelectedCustomerIds([]); // <-- ADD THIS
    }
  }, [selectedGroup]);

  // ====================== Functions ======================
  // show snackbar
  const showSnackbar = (severity, msg) => {
    setSnackbarSeverity(severity);
    setSnackbarMsg(msg);
    setOpenSnackbar(true);
  };

  // Function to handle creation of a new group
  const handleAddGroup = async () => {
    const exists = groups.some(
      (g) =>
        (g.group_name ?? g.name ?? "").toLowerCase().trim() ===
        newGroupName.toLowerCase().trim(),
    );

    if (exists) {
      showSnackbar("warning", "Group already exists");
      return;
    }

    if (!newGroupName.trim()) {
      showSnackbar("warning", "Please enter a group name");
      return;
    }

    try {
      const res = await axios.post(`${API_BASE_URL}/api/auth/user-groups`, {
        group_name: newGroupName,
        mfr_id: mfrId,
      });

      // best-effort to extract created group from response
      const created =
        res?.data?.data ?? // common pattern
        res?.data?.group ??
        res?.data; // fallback

      if (created) {
        // if API returned the created group object, append it
        // some APIs return an object, some return array - handle both
        if (Array.isArray(created)) {
          setGroups((prev) => [...prev, ...created]);
        } else {
          setGroups((prev) => [...prev, created]);
        }
      } else {
        // If API didn't return created group, fetch groups again
        try {
          const refetch = await axios.get(
            `${API_BASE_URL}/api/auth/user-groups/${mfrId}`,
          );
          setGroups(refetch?.data?.data ?? []);
        } catch (e) {
          // silent fallback
          console.error("Failed to re-fetch groups after create:", e);
        }
      }

      setNewGroupName("");
      showSnackbar("success", "Group created successfully!");
    } catch (err) {
      console.error("Error adding group:", err);
      showSnackbar("error", "Failed to create group");
    }
  };

  // Function to handle deletion of a group
  const handleDeleteGroup = async (id) => {
    if (!id) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/auth/user-groups/${id}`);
      // remove from local state immediately
      setGroups((prev) => prev.filter((g) => g.id !== id && g._id !== id));
      if (selectedGroup?.id === id || selectedGroup?._id === id) {
        setSelectedGroup(null);
        setCustomersAdded([]);
      }
      showSnackbar("success", "Group deleted");
    } catch (err) {
      console.error("Error deleting group:", err);
      showSnackbar("error", "Failed to delete group");
    }
  };

  // Function to handle selection of a customer from the Autocomplete and add them to the group
  const handleAddCustomer = async () => {
    if (!selectedGroup) {
      showSnackbar("warning", "Please select a group first");
      return;
    }

    // For Option A we only add existing customers (selectedCustomer must be an object with id)
    if (!selectedCustomer || !selectedCustomer.id) {
      showSnackbar(
        "warning",
        "Please select an existing customer from the list",
      );
      return;
    }

    try {
      const payload = {
        id: selectedGroup.id,
        group_name: selectedGroup.group_name,
        added_users: [selectedCustomer.id],
        mfr_id: mfrId,
      };

      const res = await axios.patch(
        `${API_BASE_URL}/api/auth/user-groups`,
        payload,
      );

      if (res.data?.success) {
        // Always refetch — ensures consistent full user objects
        await fetchCustomersInGroup(selectedGroup.id);

        // Clear selection and form immediately
        setSelectedCustomer(null);
        setSearchTerm("");
        setFormData({ id: "", name: "", mobile: "", email: "", note: "" });

        showSnackbar("success", "Customer added to group");
      } else {
        throw res.data;
      }
    } catch (err) {
      console.error("Error adding customer to group:", err);
      showSnackbar("error", "Failed to add customer");
    }
  };

  // Function to delete a customer from the group
  const deleteCustomersFromGroup = async (ids) => {
    if (!selectedGroup || !ids || ids.length === 0) return;

    try {
      const payload = {
        group_id: selectedGroup.id,
        user_ids: ids,
      };

      const res = await axios.patch(
        `${API_BASE_URL}/api/auth/user-groups/remove-users`,
        payload,
      );

      if (res.data?.success) {
        // remove deleted customers from table
        setCustomersAdded((prev) =>
          prev.filter((c) => !ids.includes(c.id ?? c._id)),
        );

        // clear selected checkboxes if any
        setSelectedCustomerIds([]);

        showSnackbar(
          "success",
          ids.length === 1
            ? "Customer removed from group"
            : "Customers removed from group",
        );
      } else {
        throw res.data;
      }
    } catch (err) {
      console.error("Error deleting customers:", err);
      showSnackbar("error", "Failed to remove customers");
    }
  };

  // Select/Deselect All Customer Rows
  const handleToggleSelectAll = (checked) => {
    if (checked) {
      const allIds = customersAdded.map((c) => c.id ?? c._id);
      setSelectedCustomerIds(allIds);
    } else {
      setSelectedCustomerIds([]);
    }
  };

  // Select/Deselect One Customer Row
  const handleToggleRow = (id) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // ====================== WP Message Functions ======================
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setWaFile(file); // original file
    setWaFileName(file.name);

    // Preview for images
    const isImg = file.type.startsWith("image/");
    setWaIsImage(isImg);

    // Create blob URL for preview (not for upload)
    const previewUrl = URL.createObjectURL(file);
    setWaFilePreview(previewUrl);
  };

  const uploadWAFile = async (file) => {
    const formData = new FormData();
    formData.append("pdf", file);

    const url = `${API_BASE_URL}/api/auth/uploadpdf`;

    const res = await fetch(url, {
      method: "POST",
      body: formData,
    });

    const text = await res.text();
    let parsed;

    try {
      parsed = JSON.parse(text);
    } catch (err) {
      throw new Error("Upload failed — invalid server response");
    }

    // ADD THIS:
    if (!parsed.success) {
      throw new Error(parsed.message || "Upload failed");
    }

    return parsed?.data?.file_url;
  };

  // Handle Send
  const handleSendWA = async () => {
    if (isSendingWA) return; // prevent double click

    try {
      if (!allCustomerPhone?.length) {
        showWASnackbar("warning", "No phone numbers found");
        return;
      }

      if (!waText && !waFile) {
        showWASnackbar("warning", "Message or attachment is required");
        return;
      }

      setIsSendingWA(true);

      let fileUrl = null;

      // ---------- UPLOAD FILE ----------
      if (waFile) {
        fileUrl = await uploadWAFile(waFile);
      }

      // ---------- SEND WHATSAPP ----------
      const requests = allCustomerPhone.map((phone) => {
        const payload = {
          token: "d98jl1d2fzhvaj6v32nk606x",
          phone,
          message: waText,
          fileUrl,
        };

        return axios.post(`${API_BASE_URL}/api/auth/sendwhatsppfile`, payload);
      });

      const results = await Promise.allSettled(requests);

      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;

      const failedCount = results.length - successCount;

      if (failedCount === 0) {
        showWASnackbar(
          "success",
          `WhatsApp sent successfully to ${successCount} users`,
        );
      } else {
        showWASnackbar(
          "warning",
          `Sent to ${successCount} users, failed for ${failedCount}`,
        );
      }

      // Reset & close dialog
      setIsWhatsappDialogOpen(false);
      setWaText("");
      setWaFile(null);
      setWaFilePreview(null);
      setWaFileName("");
      setWaIsImage(false);
    } catch (err) {
      console.error("Unexpected error:", err);
      showWASnackbar("error", "Failed to send WhatsApp message");
    } finally {
      setIsSendingWA(false);
    }
  };

  const showWASnackbar = (severity, msg) => {
    setWaSnackbarSeverity(severity);
    setWaSnackbarMsg(msg);
    setWaSnackbarOpen(true);
  };

  // ====================== Table Styling ======================
  const headerCell = {
    color: "#fff",
    fontWeight: 600,
    borderBottom: "1px solid #516272",
  };

  const bodyCell = {
    color: "#fff",

    // Long text handling
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  // ====================== Filtered Groups ======================
  const filteredGroups = groups.filter((g) =>
    (g.group_name ?? g.name ?? "")
      .toLowerCase()
      .includes(newGroupName.toLowerCase()),
  );

  return (
    <Box
      display="flex"
      bgcolor="#394A5C"
      width="100%"
      minHeight="100vh"
      color="white"
      p={3}
    >
      {/* Sidebar - Group Management */}
      <Box
        sx={{
          width: "25%",
          minWidth: 300, // prevents collapsing on small screens
          maxHeight: "calc(100vh - 48px)", // keeps within screen
          bgcolor: "#1E2F40",
          p: 1.5,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Navigate Home & Navigate Back Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          {/* Back Button */}
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#e0e0e0",
                "& svg": {
                  fill: "#056b05ff",
                },
              },
            }}
          >
            <ArrowBackIosNewRoundedIcon
              onClick={() => navigate(-1)}
              sx={{
                fontSize: 20,
                fill: "#42A996",
                transition: "all 0.2s ease",
              }}
            />
          </Box>

          {/* Home Button */}
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 3,
              bgcolor: "#FFF",
              p: 0.5,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                bgcolor: "#e0e0e0",
                "& svg": {
                  fill: "#056b05ff",
                },
              },
            }}
          >
            <HomeRoundedIcon
              onClick={() => navigate("/admin-dash")}
              sx={{
                fontSize: 20,
                fill: "#42A996",
                transition: "all 0.2s ease",
              }}
            />
          </Box>
        </Box>

        {/* Main Heading */}
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 600,
            mb: 1,
          }}
        >
          All Groups
        </Typography>

        {/* Input and Button for Add/Create Group */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <TextField
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Search or create group"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            sx={{
              height: "40px",
              input: {
                bgcolor: "#fff",
                borderRadius: 1,
                color: "#595959",
                "&::placeholder": {
                  fontSize: 14,
                  color: "rgba(114, 114, 114, 1)",
                  opacity: 1,
                },
              },
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": { borderColor: "#8FCFCF" },
                "&.Mui-focused fieldset": { borderColor: "#0F8A8F" },
                backgroundColor: "#1E2F40",
              },
            }}
          />

          {/* Add Group Button */}
          <Box
            onClick={handleAddGroup}
            sx={{
              borderRadius: 1.4,
              bgcolor: "#3DDAB4",
              height: "40px",
              width: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              "&:hover": {
                bgcolor: "#29957aff",
              },
            }}
          >
            <Add sx={{ color: "#fff", fontWeight: "bold", fontSize: 28 }} />
          </Box>
        </Box>

        {/* Group List (Maps 'groups' state) */}
        <List
          dense
          sx={{
            flex: 1,
            overflowY: "auto",
            maxHeight: "calc(100vh - 180px)",

            // Hide scrollbar (Chrome, Edge, Safari)
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },

            // Hide scrollbar (Firefox)
            scrollbarWidth: "none",

            // Hide scrollbar (IE / old Edge)
            msOverflowStyle: "none",
          }}
        >
          {[...filteredGroups]
            .sort((a, b) =>
              (a.group_name ?? a.name ?? "").localeCompare(
                b.group_name ?? b.name ?? "",
              ),
            )
            .map((g, index) => (
              <React.Fragment key={g.id ?? g._id ?? index}>
                <ListItem
                  onClick={() => setSelectedGroup(g)} // Select group
                  sx={{
                    my: 0.5,
                    bgcolor:
                      selectedGroup?.id === g.id || selectedGroup?._id === g._id
                        ? "#1E2F40"
                        : "#32404e",
                    color:
                      selectedGroup?.id === g.id || selectedGroup?._id === g._id
                        ? "#000"
                        : "#fff",
                    borderRadius: 1,
                    cursor: "pointer",
                    "&:hover": { bgcolor: "#516272" },
                  }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGroup(g.id ?? g._id);
                      }}
                    >
                      <DeleteOutlineIcon
                        sx={{ color: "#FF6B6B", fontSize: 24 }}
                      />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={g?.group_name ?? g.name ?? "Unnamed Group"}
                    sx={{ color: "white" }}
                  />
                </ListItem>

                {index < groups.length - 1 && (
                  <Box
                    sx={{
                      height: "1px",
                      bgcolor: "rgba(255, 255, 255, 0.15)",
                      mx: 1,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
        </List>
      </Box>

      {/* Main Content - Customer Search and Table */}
      {!selectedGroup && (
        <Box
          sx={{
            width: "75%",
            ml: 3,
          }}
          bgcolor="#4B5C6F"
          p={3}
          borderRadius={2}
        >
          <Typography
            variant="h5"
            sx={{ textAlign: "center", mt: 4, color: "rgb(144, 161, 189)" }}
          >
            Please Select a Group
          </Typography>
        </Box>
      )}

      {selectedGroup && (
        <Box
          sx={{
            width: "75%",
            ml: 3,
          }}
          bgcolor="#4B5C6F"
          p={3}
          borderRadius={2}
        >
          {/* Search Customer, Group Name & WP Button */}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              alignItems: "flex-end",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Search Customer
            </Typography>

            {/* Group Name & WP Button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-end",
                flexDirection: "column",
              }}
            >
              {/* Group Name */}
              <Typography variant="h6" sx={{ color: "#FFF", fontWeight: 600 }}>
                {selectedGroup?.group_name}
              </Typography>

              {/* WP Button */}
              <Button
                onClick={() => setIsWhatsappDialogOpen(true)}
                sx={{
                  mt: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  background: "#2dc464",
                  color: "white",
                  fontWeight: 600,
                  px: 2,
                  py: 1,
                  borderRadius: "10px",
                  textTransform: "none",
                  "&:hover": {
                    background: "#25ac58",
                  },
                }}
              >
                Send Message
                <WhatsAppIcon sx={{ fontSize: 24 }} />
              </Button>
            </Box>
          </Box>

          {/* Customer Details Field & Add Customer Button */}
          <Box mt={2}>
            {/* Autocomplete for Customer Search */}
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ position: "relative", width: 500 }}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Search by Name"
                  value={searchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchTerm(value);
                    setIsDropdownOpen(true);
                    setHighlightedIndex(-1);
                  }}
                  onFocus={() => {
                    if (customers && customers.length > 0) {
                      setIsDropdownOpen(true);
                    }
                  }}
                  onKeyDown={(e) => {
                    const lower = searchTerm.trim().toLowerCase();
                    const filtered = lower
                      ? customers.filter((opt) =>
                          opt.name?.toLowerCase().includes(lower),
                        )
                      : customers;

                    if (e.key === "ArrowDown") {
                      e.preventDefault();
                      setHighlightedIndex((prev) =>
                        prev < filtered.length - 1 ? prev + 1 : prev,
                      );
                    } else if (e.key === "ArrowUp") {
                      e.preventDefault();
                      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
                    } else if (e.key === "Enter" && highlightedIndex >= 0) {
                      e.preventDefault();
                      const option = filtered[highlightedIndex];
                      if (option) {
                        setSelectedCustomer(option);
                        setSearchTerm(option.name);
                        setFormData({
                          id: option.id,
                          name: option.name,
                          mobile: option.mobile,
                          email: option.email ?? "",
                          note: "",
                        });
                        setIsDropdownOpen(false);
                        setHighlightedIndex(-1);
                      }
                    } else if (e.key === "Escape") {
                      setIsDropdownOpen(false);
                      setHighlightedIndex(-1);
                    }
                  }}
                  sx={{
                    bgcolor: "#394A5C",
                    borderRadius: 1,
                    input: {
                      color: "white",
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.6)",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#989898" },
                      "&:hover fieldset": { borderColor: "#989898" },
                      "&.Mui-focused fieldset": { borderColor: "#989898" },
                    },
                  }}
                  InputProps={{
                    endAdornment: searchTerm && (
                      <IconButton
                        onClick={() => {
                          setSelectedCustomer(null);
                          setSearchTerm("");
                          setFormData({
                            id: "",
                            name: "",
                            mobile: "",
                            email: "",
                            note: "",
                          });
                          setIsDropdownOpen(false);
                          setHighlightedIndex(-1);
                        }}
                        size="small"
                      >
                        <Close sx={{ color: "white" }} />
                      </IconButton>
                    ),
                  }}
                />

                {isDropdownOpen &&
                  (() => {
                    const lower = searchTerm.trim().toLowerCase();
                    const filtered = lower
                      ? customers.filter((opt) =>
                          opt.name?.toLowerCase().includes(lower),
                        )
                      : customers;

                    // Remove duplicates based on id
                    const uniqueFiltered = filtered.reduce((acc, current) => {
                      const existingItem = acc.find(
                        (item) =>
                          (item.id ?? item._id) === (current.id ?? current._id),
                      );
                      if (!existingItem) {
                        acc.push(current);
                      }
                      return acc;
                    }, []);

                    return uniqueFiltered.length > 0 ? (
                      <ClickAwayListener
                        onClickAway={() => {
                          setIsDropdownOpen(false);
                          setHighlightedIndex(-1);
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            mt: 0.5,
                            maxHeight: 300,
                            overflowY: "auto",
                            bgcolor: "#2B3A4A",
                            borderRadius: 1,
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                            zIndex: 1300,
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                          }}
                        >
                          {uniqueFiltered.map((option, index) => {
                            const optionId = option.id ?? option._id ?? "";
                            const uniqueKey = `${optionId}-${index}`;
                            const isAlreadyAdded = customersAdded.some(
                              (u) => (u.id ?? u._id ?? "") === optionId,
                            );
                            const isHighlighted = index === highlightedIndex;

                            return (
                              <Box
                                key={uniqueKey}
                                onClick={() => {
                                  setSelectedCustomer(option);
                                  setSearchTerm(option.name);
                                  setFormData({
                                    id: option.id,
                                    name: option.name,
                                    mobile: option.mobile,
                                    email: option.email ?? "",
                                    note: "",
                                  });
                                  setIsDropdownOpen(false);
                                  setHighlightedIndex(-1);
                                }}
                                onMouseEnter={() => setHighlightedIndex(index)}
                                sx={{
                                  padding: 0,
                                  color: "white",
                                  bgcolor: isAlreadyAdded
                                    ? "#182838ff"
                                    : isHighlighted
                                      ? "rgba(255, 255, 255, 0.08)"
                                      : "#2B3A4A",
                                  width: "100%",
                                  cursor: "pointer",
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    bgcolor: isAlreadyAdded
                                      ? "#182838ff"
                                      : "rgba(255, 255, 255, 0.08)",
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    px: 1.5,
                                    py: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <Box>
                                    <strong>{option.name}</strong>
                                    <Box
                                      sx={{ fontSize: 12, color: "#A9B6C3" }}
                                    >
                                      {option.mobile}
                                    </Box>
                                  </Box>

                                  {isAlreadyAdded && (
                                    <CheckCircleRoundedIcon
                                      sx={{
                                        color: "#4CAF50",
                                        fontSize: 22,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </ClickAwayListener>
                    ) : searchTerm ? (
                      <ClickAwayListener
                        onClickAway={() => {
                          setIsDropdownOpen(false);
                          setHighlightedIndex(-1);
                        }}
                      >
                        <Box
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            mt: 0.5,
                            bgcolor: "#2B3A4A",
                            borderRadius: 1,
                            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.3)",
                            zIndex: 1300,
                            p: 2,
                            color: "#A9B6C3",
                            textAlign: "center",
                          }}
                        >
                          No customers found
                        </Box>
                      </ClickAwayListener>
                    ) : null;
                  })()}
              </Box>
            </Box>

            {/* Mobile and Email Fields (Autofill based on selectedCustomer) */}
            <Box sx={{ mt: 1, gap: 1, display: "flex" }}>
              <TextField
                name="mobile"
                size="small"
                variant="outlined"
                placeholder="Mobile"
                value={formData?.mobile || ""} // Autofills with mobile from selected customer
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData((prev) => ({ ...prev, [name]: value }));
                }}
                sx={{
                  width: 160,
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    backgroundColor: "#394A5C",
                    "& fieldset": { borderColor: "#989898" },
                    "&:hover fieldset": { borderColor: "#B0B0B0" },
                    "&.Mui-focused fieldset": { borderColor: "#989898" },
                    "& input": {
                      height: 40,
                      padding: "0 14px",
                      color: "white",
                      boxSizing: "border-box",
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.6)",
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
              <TextField
                size="small"
                variant="outlined"
                placeholder="Email"
                name="email"
                value={formData?.email || ""} // Autofills with mobile from selected customer
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData((prev) => ({ ...prev, [name]: value }));
                }}
                sx={{
                  width: 332,
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    backgroundColor: "#394A5C",
                    "& fieldset": { borderColor: "#989898" },
                    "&:hover fieldset": { borderColor: "#B0B0B0" },
                    "&.Mui-focused fieldset": { borderColor: "#989898" },
                    "& input": {
                      height: 40,
                      padding: "0 14px",
                      color: "white",
                      boxSizing: "border-box",
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.6)",
                        opacity: 1,
                      },
                    },
                  },
                }}
              />
            </Box>

            {/* Note Field and "Add Customer" Button */}
            <Box sx={{ mt: 1 }}>
              <TextField
                size="small"
                variant="outlined"
                placeholder="Note"
                name="note"
                value={formData?.note || ""}
                onChange={(e) => {
                  const { name, value } = e.target;
                  setFormData((prev) => ({ ...prev, [name]: value }));
                }}
                sx={{
                  width: 500,
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    backgroundColor: "#394A5C",
                    "& fieldset": { borderColor: "#989898" },
                    "&:hover fieldset": { borderColor: "#B0B0B0" },
                    "&.Mui-focused fieldset": { borderColor: "#989898" },
                    "& input": {
                      height: 40,
                      padding: "0 14px",
                      color: "white",
                      boxSizing: "border-box",
                      "&::placeholder": {
                        color: "rgba(255, 255, 255, 0.6)",
                        opacity: 1,
                      },
                    },
                  },
                }}
              />{" "}
              <Button
                onClick={handleAddCustomer}
                variant="contained"
                sx={{
                  ml: 0.5,
                  height: "40px",
                  bgcolor: "#3DDAB4",
                  "&:hover": { bgcolor: "#2BC19C" },
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Add Customer
                </Typography>
              </Button>
            </Box>
          </Box>

          {/* Customer Table */}
          <Box sx={{ width: "100%", overflowX: "auto" }}>
            <Typography sx={{ mt: 3, fontWeight: 500, mb: 1 }}>
              Added Customers
            </Typography>

            <TableContainer
              component={Paper}
              sx={{
                bgcolor: "#2B3A4A",
                borderRadius: 2,
                overflowX: "auto",

                // scrollbar styling (optional but nice)
                "&::-webkit-scrollbar": { height: 8 },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#516272",
                  borderRadius: 4,
                },
              }}
            >
              <Table
                sx={{
                  width: "100%",
                  minWidth: 600, // ensures scroll instead of compression on mobile
                  tableLayout: "fixed",
                }}
              >
                {/* Column sizing */}
                <colgroup>
                  <col style={{ width: "40%" }} />
                  <col style={{ width: "20%" }} />
                  <col style={{ width: "25%" }} />
                  <col style={{ width: "15%" }} />
                </colgroup>

                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCell}>Name</TableCell>
                    <TableCell sx={headerCell}>Mobile No.</TableCell>
                    <TableCell sx={headerCell}>Added On</TableCell>
                    <TableCell sx={{ ...headerCell, textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Checkbox
                          checked={allSelected}
                          onChange={(e) =>
                            handleToggleSelectAll(e.target.checked)
                          }
                          size="small"
                          sx={{ color: "#fff" }}
                        />

                        <Typography sx={{ fontSize: 13 }}>
                          {allSelected ? "Deselect All" : "Select All"}
                        </Typography>

                        {selectedCustomerIds.length > 0 && (
                          <Tooltip title="Delete selected">
                            <IconButton
                              size="small"
                              onClick={() =>
                                deleteCustomersFromGroup(selectedCustomerIds)
                              }
                            >
                              <Delete sx={{ color: "#FF6B6B" }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {customersAdded?.map((cust) => {
                    const id = cust.id ?? cust._id;

                    return (
                      <TableRow
                        key={id}
                        onClick={() => handleToggleRow(id)}
                        sx={{
                          cursor: "pointer",
                          bgcolor: highlightedIds.includes(id)
                            ? "#3ddab36b"
                            : "transparent",
                          "&:hover": {
                            bgcolor: highlightedIds.includes(id)
                              ? "#3ddab3cb"
                              : "#516272",
                          },
                          transition: "background-color 0.25s ease",
                        }}
                      >
                        <TableCell sx={bodyCell}>{cust.name}</TableCell>

                        <TableCell sx={bodyCell}>{cust.mobile}</TableCell>

                        <TableCell sx={bodyCell}>
                          {formatDateTime(cust?.created_at ?? cust?.createdAt)}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          <Checkbox
                            size="small"
                            checked={selectedCustomerIds.includes(id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleToggleRow(id)}
                            sx={{ color: "#fff" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      )}

      {/* Global Snackbar for alerts/messages */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      {/* WhatsApp Message */}
      <Dialog
        open={isWhatsappDialogOpen}
        onClose={() => setIsWhatsappDialogOpen(false)}
        PaperProps={{
          sx: {
            width: 420,
            bgcolor: "#0B141A",
            color: "white",
            borderRadius: 3,
            backdropFilter: "blur(10px)",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(4px)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            bgcolor: "#075E54",
            color: "white",
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
          }}
        >
          <WhatsAppIcon sx={{ fontSize: 28 }} />
          WhatsApp Message
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* TEXT FIELD */}
          <TextField
            fullWidth
            multiline
            rows={6}
            placeholder="Type your WhatsApp message..."
            value={waText}
            onChange={(e) => setWaText(e.target.value)}
            sx={{
              bgcolor: "#1F2C33",
              borderRadius: 2,
              "& .MuiInputBase-input": { color: "white" },
            }}
          />

          {/* FILE UPLOAD (ANY FILE) */}
          <Button
            component="label"
            variant="contained"
            sx={{
              mt: 2,
              bgcolor: "#25D366",
              color: "white",
              textTransform: "none",
              "&:hover": { bgcolor: "#1EBE5A" },
              borderRadius: 2,
            }}
          >
            Add attachment
            <input type="file" hidden accept="*" onChange={handleFileUpload} />
          </Button>

          {/* PREVIEW */}
          {waFile && (
            <Box mt={2}>
              {waIsImage ? (
                <Box
                  sx={{
                    width: "100%",
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid #128C7E",
                  }}
                >
                  <img
                    src={waFilePreview}
                    alt="preview"
                    style={{ width: "100%", height: "auto" }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: "#132028",
                    borderRadius: 2,
                    border: "1px solid #128C7E",
                  }}
                >
                  <Typography sx={{ color: "#ddd", fontSize: 14 }}>
                    📄 {waFileName}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => {
              setIsWhatsappDialogOpen(false);
              setWaText("");
              setWaFile(null);
              setWaFilePreview(null);
              setWaFileName("");
              setWaIsImage(false);
            }}
            sx={{
              color: "white",
              border: "1px solid #555",
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleSendWA}
            disabled={isSendingWA}
            sx={{
              bgcolor: "#25D366",
              color: "white",
              px: 4,
              fontWeight: 600,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: "#1EBE5A" },
              "&.Mui-disabled": {
                bgcolor: "#1EBE5A",
                opacity: 0.7,
              },
            }}
          >
            {isSendingWA ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* WhatsApp Snackbar */}
      <Snackbar
        open={waSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setWaSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setWaSnackbarOpen(false)}
          severity={waSnackbarSeverity}
          sx={{ width: "100%" }}
        >
          {waSnackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default UsersGroupMaster;
